/**
 * Account · Integrations — the signed-in user's OWN Procore and Autodesk
 * sign-ins, from the design's `Led AI - Project Details.dc.html` (project
 * `806e74fa`): a right-hand drawer opened from the account menu.
 *
 * ## Per user, and the same sign-in as the phone
 *
 * These are not workspace settings. Led AI creates records "as you" in the
 * provider, so every person signs in once per system — and it is the SAME
 * sign-in the mobile app uses: signing out here signs the phone out too, which
 * the drawer says before it does it.
 *
 * ## How a sign-in completes
 *
 * The provider's login opens in a new tab (never a frame — Procore and APS
 * refuse to be framed, and a company that forces a specific browser for its
 * logins gets the browser this console already runs in). The backend's
 * callback ends on a "you can close this tab" page (`returnMode=console`), and
 * this drawer learns the outcome by polling the sign-in's single-use attempt,
 * which both providers now keep (LED-870). Status polling remains only as the
 * fallback for an older backend that returns no attempt.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ApiError,
  cancelSignInAttempt,
  chooseProcoreCompany,
  fetchIntegrations,
  readSignInAttempt,
  signOutOfIntegration,
  startIntegrationSignIn,
  type IntegrationProvider,
  type IntegrationsResponse,
} from "../api";
import { DS, FONT_MONO } from "../tokens";
import { DsIcon, DsPill } from "../views/TeamProcore";
import { SourceMark } from "../views/ProjectDetails";

export { OPEN_INTEGRATIONS_EVENT, openIntegrations } from "./integrationsEvents";

/** Where each provider's login may live. A start response naming any other
 *  host is refused rather than opened. */
const LOGIN_HOSTS: Record<IntegrationProvider, string[]> = {
  procore: ["login.procore.com", "login-sandbox.procore.com"],
  autodesk: ["developer.api.autodesk.com"],
};

/** A sign-in the drawer can run: either provider, or Procore's developer
 *  sandbox (offered to Led AI staff only, under the Procore card). */
type SignInTarget = IntegrationProvider | "sandbox";
const providerOf = (target: SignInTarget): IntegrationProvider => (target === "sandbox" ? "procore" : target);
const targetName = (target: SignInTarget) => (target === "sandbox" ? "Procore sandbox" : target === "procore" ? "Procore" : "Autodesk");

const POLL_MS = 2000;
/** With no attempt to expire (an older backend), the drawer gives up waiting. */
const AUTODESK_WAIT_MS = 10 * 60_000;

const CARDS: Array<{ id: IntegrationProvider; name: string; sub: string }> = [
  { id: "procore", name: "Procore", sub: "Creates daily logs, observations, punch items, tasks and photos as you in Procore projects." },
  { id: "autodesk", name: "Autodesk Construction Cloud", sub: "Creates issues and daily report forms as you in ACC projects." },
];

const EYEBROW: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", color: DS.meta, textTransform: "uppercase" };
const RULE = "1px solid rgba(10,10,10,.07)";
const ELLIPSIS: React.CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

/** Both providers store `linkedAt` in Unix SECONDS; read as milliseconds it
 *  printed "Jan 21, 1970". Milliseconds are still accepted, in case one
 *  provider's status ever switches. */
const day = (at?: number | null) => {
  if (!at) return "—";
  const ms = at < 1e12 ? at * 1000 : at;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/** The last status this session saw, so reopening the drawer shows it at once
 *  (while it refreshes) instead of a moment of every card looking signed out. */
let lastStatus: IntegrationsResponse | null = null;
/** Tests only: forget the session's last status. */
export function resetIntegrationsStatusCache() {
  lastStatus = null;
}

/** When this target's sign-in landed, or null while it isn't signed in. */
function signedInAt(data: IntegrationsResponse | null, target: SignInTarget): number | null {
  const s = target === "sandbox" ? data?.procore?.sandbox : target === "procore" ? data?.procore?.production : data?.autodesk;
  return s?.connected && !s.reconnectRequired ? (s.linkedAt ?? 0) : null;
}

interface CardState {
  on: boolean;
  reconnect: boolean;
  reason?: string | null;
  linkedAt?: number | null;
  company?: string | null;
  companies?: Array<{ id: number; name: string }>;
  needsCompany?: boolean;
  /** Procore: signed in to the developer sandbox only (no production sign-in). */
  sandboxOnly?: boolean;
}

function cardState(data: IntegrationsResponse | null, id: IntegrationProvider): CardState | null {
  if (!data) return null;
  if (id === "procore") {
    const p = data.procore?.production;
    if (!p) return null;
    const sb = data.procore?.sandbox;
    const prodOn = p.connected && !p.reconnectRequired;
    // A sandbox-only sign-in IS a Procore sign-in: the card reads signed in,
    // with the sandbox's company, rather than offering a sign-in it doesn't need.
    if (!prodOn && sb?.connected && !sb.reconnectRequired) {
      return {
        on: true,
        reconnect: false,
        linkedAt: sb.linkedAt,
        company: sb.companyName,
        companies: sb.availableCompanies,
        needsCompany: !!sb.needsCompanySelection,
        sandboxOnly: true,
      };
    }
    return {
      on: prodOn,
      reconnect: !!p.reconnectRequired,
      reason: p.reconnectReason,
      linkedAt: p.linkedAt,
      company: p.companyName,
      companies: p.availableCompanies,
      needsCompany: !!p.needsCompanySelection,
    };
  }
  const a = data.autodesk;
  if (!a) return null;
  return { on: a.connected && !a.reconnectRequired, reconnect: !!a.reconnectRequired, linkedAt: a.linkedAt };
}

export function IntegrationsDrawer({
  open,
  onClose,
  accountName,
  onChanged,
  showSandbox = false,
}: {
  open: boolean;
  onClose: () => void;
  accountName: string;
  /** Offer the Procore developer sandbox sign-in (Led AI staff only). */
  showSandbox?: boolean;
  /** A sign-in or sign-out landed — anything provider-backed should re-read. */
  onChanged: () => void;
}) {
  const [data, setData] = useState<IntegrationsResponse | null>(lastStatus);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<SignInTarget | null>(null);
  const [blockedUrl, setBlockedUrl] = useState<{ provider: SignInTarget; url: string } | null>(null);
  const [confirmOut, setConfirmOut] = useState<IntegrationProvider | null>(null);
  const [problem, setProblem] = useState<{ provider: SignInTarget; message: string } | null>(null);
  const [toast, setToast] = useState("");
  const watch = useRef<{ cancel: () => void } | null>(null);
  /** The Procore attempt being watched — cancelled server-side on Cancel. */
  const attempt = useRef<string | undefined>(undefined);
  const changed = useRef(onChanged);
  changed.current = onChanged;

  const load = useCallback(async () => {
    try {
      const next = await fetchIntegrations();
      lastStatus = next;
      setData(next);
      setFailure(null);
      // A message about a sign-in that has since landed is stale.
      setProblem((p) => (p && signedInAt(next, p.provider) !== null ? null : p));
      return next;
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "Could not load your sign-ins.");
      return null;
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Reopening starts clean: last time's failure is not this time's news.
    if (!watch.current) setProblem(null);
    void load();
  }, [open, load]);
  useEffect(() => () => watch.current?.cancel(), []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const finished = (provider: SignInTarget, message: string, ok: boolean) => {
    watch.current?.cancel();
    watch.current = null;
    setBusy(null);
    setBlockedUrl(null);
    if (ok) {
      setToast(message);
      changed.current();
    } else setProblem({ provider, message });
    void load();
  };

  /** Poll until the sign-in lands, fails, or is abandoned. Also re-checks the
   *  moment the reader comes back to this tab. */
  const startWatching = (provider: SignInTarget, attemptId: string | undefined, expiresAt: number | undefined, before: number | null | undefined) => {
    watch.current?.cancel();
    let stopped = false;
    const deadline = expiresAt ?? Date.now() + AUTODESK_WAIT_MS;
    const name = targetName(provider);
    /** The provider can save a sign-in its attempt never hears about — the
     *  callback reached another backend on the same database (the agent test
     *  bed's does). The status is the truth: a fresh sign-in there is a win. */
    const landed = async () => {
      const at = signedInAt(await fetchIntegrations(), provider);
      return at !== null && at !== (before ?? null);
    };
    const failed = async (message: string) => {
      const ok = await landed().catch(() => false);
      if (!stopped) finished(provider, ok ? `Signed in to ${name}` : message, ok);
    };
    const check = async () => {
      if (stopped) return;
      if (Date.now() > deadline) return failed(`The ${name} sign-in timed out. Try again.`);
      try {
        // Both providers hand back a single-use attempt to watch; an older
        // backend without one (Autodesk) has only the status to go on.
        if (attemptId) {
          const attempt = await readSignInAttempt(providerOf(provider), attemptId);
          if (stopped) return;
          if (attempt.status === "completed")
            return finished(provider, attempt.needsCompanySelection ? `Signed in to ${name} · choose your company` : `Signed in to ${name}`, true);
          if (attempt.status === "failed") return failed(`${name} sign-in didn't finish. Try again.`);
          if (attempt.status === "cancelled" || attempt.status === "expired") return failed(`The ${name} sign-in was cancelled or timed out.`);
        }
        // Still pending (or no attempt at all): a sign-in saved elsewhere
        // counts now, not when the attempt finally expires.
        if (await landed()) {
          if (stopped) return;
          return finished(provider, `Signed in to ${name}`, true);
        }
      } catch {
        // A blip while waiting is not a failed sign-in; the next tick retries.
      }
    };
    const timer = setInterval(() => void check(), POLL_MS);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    watch.current = {
      cancel: () => {
        stopped = true;
        clearInterval(timer);
        window.removeEventListener("focus", onFocus);
      },
    };
  };

  const signIn = async (provider: SignInTarget) => {
    setProblem(null);
    setBlockedUrl(null);
    setConfirmOut(null);
    setBusy(provider);
    const system = providerOf(provider);
    // Opened NOW, inside the click, so a popup blocker lets it through; it is
    // pointed at the provider once the backend has signed the request.
    const tab = window.open("about:blank", "_blank");
    try {
      const before = signedInAt(data, provider);
      const started = await startIntegrationSignIn(system, provider === "sandbox" ? "sandbox" : "production");
      const host = new URL(started.url).host;
      if (!LOGIN_HOSTS[system].includes(host)) throw new Error("The sign-in link was not from the provider.");
      if (tab) {
        tab.opener = null;
        tab.location.replace(started.url);
      } else {
        setBlockedUrl({ provider, url: started.url });
      }
      attempt.current = started.attemptId;
      startWatching(provider, started.attemptId, started.expiresAt, before);
    } catch (error) {
      tab?.close();
      setBusy(null);
      setProblem({
        provider,
        message:
          error instanceof ApiError && error.status === 409
            ? "A sign-in is already finishing. Give it a moment, then refresh."
            : error instanceof Error
              ? error.message
              : "Could not start the sign-in.",
      });
    }
  };

  const cancel = async (provider: SignInTarget) => {
    watch.current?.cancel();
    watch.current = null;
    setBusy(null);
    setBlockedUrl(null);
    const id = attempt.current;
    attempt.current = undefined;
    // Best effort: the attempt also expires on its own.
    if (id) await cancelSignInAttempt(providerOf(provider), id).catch(() => {});
  };

  const signOut = async (provider: IntegrationProvider) => {
    setConfirmOut(null);
    try {
      await signOutOfIntegration(provider);
      setProblem(null);
      setToast(`Signed out of ${provider === "procore" ? "Procore" : "Autodesk"}`);
      changed.current();
      void load();
    } catch (error) {
      setProblem({ provider, message: error instanceof Error ? error.message : "Could not sign out." });
    }
  };

  const pickCompany = async (companyId: number, env: "production" | "sandbox" = "production") => {
    try {
      await chooseProcoreCompany(companyId, env);
      setToast(env === "sandbox" ? "Procore sandbox company chosen" : "Procore company chosen");
      changed.current();
      void load();
    } catch (error) {
      setProblem({ provider: env === "sandbox" ? "sandbox" : "procore", message: error instanceof Error ? error.message : "Could not choose that company." });
    }
  };

  if (!open) return toast ? <Toast text={toast} /> : null;

  return createPortal(
    <>
      <div className="pd-scrim" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 45, background: "rgba(10,10,10,.3)" }} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Integrations"
        data-testid="integrations-drawer"
        className="pd-drawer"
        style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 46, width: "min(520px, 100vw)", background: DS.paper, display: "flex", flexDirection: "column", boxShadow: "-20px 0 50px rgba(10,10,10,.15)" }}
      >
        <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 12, padding: "20px 22px 16px", borderBottom: RULE }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ ...EYEBROW, ...ELLIPSIS }}>Account · {accountName}</div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.6px", marginTop: 4 }}>Integrations</div>
          </div>
          <button type="button" aria-label="Close" className="kb pd-press" onClick={onClose} style={{ width: 34, height: 34, border: 0, borderRadius: 17, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <DsIcon name="close" size={15} color={DS.ink} />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, maxHeight: "100%", overflowY: "auto", padding: "20px 22px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: DS.body2 }}>
            Sign in once per system. Led AI uses your sign-in to create records as you in any project connected to that system.
          </div>
          {failure && !data && (
            <div role="alert" style={{ padding: "12px 14px", borderRadius: 12, background: DS.redSoft, color: DS.alertInk, fontSize: 13.5 }}>{failure}</div>
          )}
          {CARDS.map((card) => {
            const state = cardState(data, card.id);
            // Until the status has been read, a card says nothing about being
            // signed in or out — no Sign in button, no sandbox link.
            if (!data) return <LoadingCard key={card.id} name={card.name} id={card.id} />;
            const on = !!state?.on;
            const waiting = busy === card.id;
            const error = problem?.provider === card.id ? problem.message : (card.id === "procore" ? data?.procoreError : data?.autodeskError);
            return (
              <div key={card.id} data-testid={`integration-${card.id}`} style={{ border: `1px solid ${on ? "rgba(0,163,104,.35)" : "rgba(10,10,10,.08)"}`, borderRadius: 18, background: DS.surface, padding: "18px 20px", transition: "border-color .15s" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <SourceMark source={card.id} size={44} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.4px" }}>{card.name}</span>
                      {on && <DsPill tone="live" dot testId={`integration-${card.id}-on`}>Signed in</DsPill>}
                      {state?.sandboxOnly && <DsPill tone="muted" testId="integration-procore-sandbox-only">Sandbox</DsPill>}
                      {state?.reconnect && <DsPill tone="warning">Sign in again</DsPill>}
                    </div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.5, color: DS.body2, marginTop: 3 }}>{card.sub}</div>
                  </div>
                  {!on && (
                    <button
                      type="button"
                      className="kb pd-press"
                      data-testid={`integration-${card.id}-signin`}
                      disabled={waiting || !data}
                      onClick={() => void signIn(card.id)}
                      style={{ flex: "none", height: 40, padding: "0 18px", border: 0, borderRadius: 20, background: DS.ink, color: DS.surface, display: "inline-flex", alignItems: "center", gap: 8, font: "inherit", fontSize: 14, fontWeight: 700, cursor: waiting ? "default" : "pointer", whiteSpace: "nowrap" }}
                    >
                      {waiting && <span aria-hidden className="pd-spin" style={{ width: 14, height: 14, boxSizing: "border-box", borderRadius: 7, border: "2px solid rgba(255,255,255,.25)", borderTopColor: DS.surface, display: "block" }} />}
                      {waiting ? "Signing in…" : state?.reconnect ? "Sign in again" : "Sign in"}
                    </button>
                  )}
                </div>

                {waiting && (
                  <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.5, color: DS.body2 }}>
                    {blockedUrl?.provider === card.id ? (
                      <>
                        Your browser blocked the new tab.{" "}
                        <a href={blockedUrl.url} target="_blank" rel="noopener noreferrer" onClick={() => setBlockedUrl(null)} style={{ color: DS.ink, fontWeight: 700 }}>
                          Open the {card.id === "procore" ? "Procore" : "Autodesk"} sign-in
                        </a>
                        .
                      </>
                    ) : (
                      <>Finish signing in on the tab that opened — this updates on its own. </>
                    )}{" "}
                    <button type="button" className="kb pd-link" onClick={() => void cancel(card.id)} style={{ border: 0, padding: 0, background: "transparent", font: "inherit", fontSize: 13, fontWeight: 600, color: DS.body2, textDecoration: "underline", cursor: "pointer" }}>
                      Cancel
                    </button>
                  </div>
                )}
                {state?.reconnect && state.reason && !waiting && <div style={{ marginTop: 10, fontSize: 13, color: DS.amber }}>{state.reason}</div>}
                {error && !waiting && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: DS.red }}>{error}</div>}

                {card.id === "procore" && state?.needsCompany && !!state.companies?.length && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: RULE }}>
                    <div style={{ ...EYEBROW, fontSize: 9 }}>Choose your Procore company</div>
                    <div style={{ display: "flex", flexDirection: "column", marginTop: 6 }}>
                      {state.companies.map((company) => (
                        <button key={company.id} type="button" className="kb pd-row" onClick={() => void pickCompany(company.id, state.sandboxOnly ? "sandbox" : "production")} style={{ display: "flex", alignItems: "center", gap: 10, height: 38, padding: "0 8px", border: 0, borderRadius: 8, background: "transparent", font: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer", textAlign: "left", color: DS.ink }}>
                          <span style={{ flex: 1, ...ELLIPSIS }}>{company.name}</span>
                          <DsIcon name="chevron" size={12} color={DS.meta} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {on && (
                  <>
                    {/* Signed in is the whole answer: no company, no project list
                        (user-directed, 2026-10-23). The footer keeps the date
                        and the way out. */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 16, paddingTop: 12, borderTop: RULE }}>
                      {confirmOut === card.id ? (
                        <>
                          <span style={{ minWidth: 0, fontSize: 13, color: DS.body2 }}>
                            {card.id === "procore" && data?.procore?.sandbox.connected
                              ? "Signs out of Procore and the Procore sandbox, on your phone too, and pauses sync for its projects."
                              : "Signs out your phone too, and pauses sync for its projects."}
                          </span>
                          <span style={{ display: "flex", gap: 12, flex: "none" }}>
                            <button type="button" className="kb" onClick={() => setConfirmOut(null)} style={{ border: 0, background: "transparent", font: "inherit", fontSize: 13.5, fontWeight: 700, color: DS.body2, cursor: "pointer" }}>Keep</button>
                            <button type="button" className="kb" data-testid={`integration-${card.id}-signout-confirm`} onClick={() => void signOut(card.id)} style={{ border: 0, background: "transparent", font: "inherit", fontSize: 13.5, fontWeight: 700, color: DS.red, cursor: "pointer" }}>Sign out</button>
                          </span>
                        </>
                      ) : (
                        <>
                          <span style={{ minWidth: 0, fontFamily: FONT_MONO, fontSize: 10, fontWeight: 500, letterSpacing: "1.1px", color: DS.meta, textTransform: "uppercase", ...ELLIPSIS }}>Signed in {day(state?.linkedAt)}</span>
                          <button type="button" className="kb pd-signout" data-testid={`integration-${card.id}-signout`} onClick={() => setConfirmOut(card.id)} style={{ flex: "none", border: 0, background: "transparent", font: "inherit", fontSize: 13.5, fontWeight: 700, color: DS.body2, cursor: "pointer", whiteSpace: "nowrap" }}>
                            Sign out
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
                {/* One Procore environment at a time: the sandbox is offered
                    only with no Procore sign-in at all, and switching is a
                    Sign out first (user-directed, 2026-10-23). */}
                {card.id === "procore" && showSandbox && (!on || state?.sandboxOnly) && busy !== "procore" && (
                  <SandboxRow
                    sandboxOnly={!!state?.sandboxOnly}
                    status={data?.procore?.sandbox ?? null}
                    busy={busy === "sandbox"}
                    blockedUrl={blockedUrl?.provider === "sandbox" ? blockedUrl.url : null}
                    error={problem?.provider === "sandbox" ? problem.message : null}
                    disabled={!data || (busy !== null && busy !== "sandbox")}
                    onSignIn={() => void signIn("sandbox")}
                    onCancel={() => void cancel("sandbox")}
                    onPickCompany={(id) => void pickCompany(id, "sandbox")}
                  />
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 12.5, lineHeight: 1.55, color: DS.meta, padding: "0 4px" }}>
            A project's system is chosen when it's created and can't be changed. Signing out here pauses sync for its projects until you sign in again.
          </div>
        </div>
      </aside>
      {toast && <Toast text={toast} />}
    </>,
    document.body,
  );
}

/**
 * Procore's DEVELOPER sandbox, under the Procore card — subtle on purpose: it
 * is for Led AI staff testing against sandbox projects, never a customer
 * choice. Signing out is the card's own Sign out: the backend's Procore
 * sign-out removes both environments at once.
 */
function SandboxRow({
  sandboxOnly,
  status,
  busy,
  blockedUrl,
  error,
  disabled,
  onSignIn,
  onCancel,
  onPickCompany,
}: {
  /** The card above is showing the sandbox sign-in (no production one). */
  sandboxOnly: boolean;
  status: { connected: boolean; reconnectRequired: boolean; companyName?: string | null; needsCompanySelection?: boolean; availableCompanies?: Array<{ id: number; name: string }> } | null;
  busy: boolean;
  blockedUrl: string | null;
  error: string | null;
  disabled: boolean;
  onSignIn: () => void;
  onCancel: () => void;
  onPickCompany: (companyId: number) => void;
}) {
  const on = !!status?.connected && !status.reconnectRequired;
  const quiet: React.CSSProperties = { border: 0, padding: 0, background: "transparent", font: "inherit", fontSize: 12.5, fontWeight: 600, color: DS.body2, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" };
  return (
    <div data-testid="integration-procore-sandbox" style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dashed rgba(10,10,10,.1)", fontSize: 12.5, lineHeight: 1.5, color: DS.meta }}>
      {on && sandboxOnly ? (
        <span>
          <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "1px" }}>DEV · </span>
          Signed in to the Procore sandbox. Sign out to use production instead.
        </span>
      ) : on ? (
        <span>
          <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "1px" }}>DEV · </span>
          Procore sandbox: signed in{status?.companyName ? ` · ${status.companyName}` : ""}
        </span>
      ) : busy ? (
        <span>
          {blockedUrl ? (
            <>
              Your browser blocked the new tab.{" "}
              <a href={blockedUrl} target="_blank" rel="noopener noreferrer" style={{ color: DS.ink, fontWeight: 600 }}>Open the sandbox sign-in</a>.
            </>
          ) : (
            "Finish signing in to the Procore sandbox on the tab that opened."
          )}{" "}
          <button type="button" className="kb pd-link" onClick={onCancel} style={quiet}>Cancel</button>
        </span>
      ) : (
        <button type="button" className="kb pd-link" data-testid="integration-procore-sandbox-signin" disabled={disabled} onClick={onSignIn} style={{ ...quiet, cursor: disabled ? "default" : "pointer" }}>
          {status?.reconnectRequired ? "Sign in again to Procore sandbox (dev)" : "Sign in to Procore sandbox (dev)"}
        </button>
      )}
      {on && !sandboxOnly && status?.needsCompanySelection && !!status.availableCompanies?.length && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 6 }}>
          <span>Choose a sandbox company:</span>
          {status.availableCompanies.map((company) => (
            <button key={company.id} type="button" className="kb pd-link" onClick={() => onPickCompany(company.id)} style={quiet}>{company.name}</button>
          ))}
        </div>
      )}
      {error && !busy && <div role="alert" style={{ marginTop: 4, color: DS.red }}>{error}</div>}
    </div>
  );
}

/** A card whose status has not been read yet: the system's name and a quiet
 *  shimmer where its state will go. */
function LoadingCard({ name, id }: { name: string; id: IntegrationProvider }) {
  return (
    <div data-testid={`integration-${id}-loading`} aria-busy="true" style={{ border: "1px solid rgba(10,10,10,.08)", borderRadius: 18, background: DS.surface, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14 }}>
      <SourceMark source={id} size={44} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.4px" }}>{name}</div>
        <div className="skel" style={{ marginTop: 8, height: 10, width: "55%", borderRadius: 5, background: DS.surfaceSunk }} />
      </div>
    </div>
  );
}

function Toast({ text }: { text: string }) {
  return createPortal(
    <div role="status" data-testid="integrations-toast" className="pd-enter" style={{ position: "fixed", left: "50%", bottom: 128, transform: "translateX(-50%)", zIndex: 60, display: "flex", alignItems: "center", gap: 10, height: 44, padding: "0 18px 0 14px", borderRadius: 22, background: DS.ink, color: DS.surface, boxShadow: "0 14px 34px rgba(10,10,10,.25)" }}>
      <span style={{ width: 20, height: 20, borderRadius: 10, background: DS.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <DsIcon name="check" size={12} color={DS.surface} />
      </span>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{text}</span>
    </div>,
    document.body,
  );
}
